"use client";

import { useParams, usePathname, useRouter } from "next/navigation";
import { setGeoPrefCookie } from "@/lib/geo/geo-pref-client";
import { locales, type Locale, getLocaleConfig } from "@/config/locale";
import { iso2ToFlagEmoji, localeToFlagIso } from "@/lib/flag-emoji";
import { cn } from "@/lib/utils";

/**
 * Language selector dropdown for footer use.
 * Native <select> matching the ChannelSelect pattern.
 */
export function LanguageSelect({
	currentLocale,
	className,
	id,
	showOptionEmoji = true,
	"aria-label": ariaLabel,
	"aria-labelledby": ariaLabelledBy,
}: {
	currentLocale: Locale;
	className?: string;
	id?: string;
	showOptionEmoji?: boolean;
	"aria-label"?: string;
	"aria-labelledby"?: string;
}) {
	const router = useRouter();
	const pathname = usePathname();
	const params = useParams<{ locale?: string; channel?: string }>();

	return (
		<select
			id={id}
			aria-label={ariaLabel}
			aria-labelledby={ariaLabelledBy}
			className={cn(
				"h-10 w-fit rounded-md border border-input bg-background px-4 py-2 pr-10 text-sm",
				"text-foreground",
				"focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
				className,
			)}
			value={currentLocale}
			onChange={(e) => {
				const newLocale = e.target.value;
				const channel = params.channel ?? "eu";
				setGeoPrefCookie({ locale: newLocale, channel });
				router.push(pathname.replace(`/${currentLocale}/`, `/${newLocale}/`));
			}}
		>
			{locales.map((locale) => {
				const name = getLocaleConfig(locale).nativeName;
				return (
					<option key={locale} value={locale}>
						{showOptionEmoji ? `${iso2ToFlagEmoji(localeToFlagIso(locale))} ${name}` : name}
					</option>
				);
			})}
		</select>
	);
}
